import axios from 'axios';
import { AIRLINE_BASE_API_URL, BASE_API_URL, OCR_UPLOAD_URL } from './constants';
import { useAuthStore } from '../store/auth/authStore';
import { EncryptionHelper } from './encryption';
import { redirectHome } from './navigation';

type APIErrorResponse = {
  Errors?: string[]
}

const getErrors = (data: any) => {
  if (data.length === undefined) {
    return data;
  }

  if (typeof data.errors === 'string' || typeof data === 'string') {
    return data;
  }
  const errors = data && data?.map((error: any) => (error.Errors.join(', ')));
  return errors.join(', ');
};

const processAPIError = (e: any) => {
  let errorMessage: any = '';
  let errorStatus = 500;

  if (axios.isAxiosError(e) && e.response && e.response.data) {
    if (e.response) {
      // Response received from server
      errorStatus = e.response.status;
      const apiRe = e.response.data as APIErrorResponse;
      if (errorStatus === 500) {
        errorMessage = 'Sorry, we are unable to complete the request. Please try again';
      } else {
        errorMessage = apiRe.Errors ? apiRe.Errors.join(', ') : getErrors(e.response.data);
      }
    } else if (e.request) {
      // No response received
      errorMessage = 'No response from server';
    } else {
      // Error in request setup
      errorMessage = 'Unable to process request';
    }
  }
  // App Error in request
  return { status: errorStatus, data: errorMessage };
};

const getRequestData = () => {
  const auth = JSON.parse(localStorage.getItem('trips_auth') || '{}');

  const Authorization = `Bearer ${auth.Token}`;
  return {
    baseUrl: BASE_API_URL, Authorization, MerchantCode: auth.MerchantCode, Hash: auth.Hash,
  };
};

let isRefreshing = false;
let refreshQueue: ((token: string | null) => void)[] = [];

const handleB2cTokenRefresh = (): Promise<string | null> => {
  if (isRefreshing) {
    return new Promise((resolve) => {
      refreshQueue.push((token) => resolve(token));
    });
  }

  isRefreshing = true;
  return new Promise(async (resolve) => {
    try {
      const refreshToken = useAuthStore.getState().refreshToken;
      if (!refreshToken) {
        useAuthStore.getState().logout();
        // redirectHome();
        resolve(null);
        isRefreshing = false;
        refreshQueue.forEach((cb) => cb(null));
        refreshQueue = [];
        return;
      }

      const { baseUrl, Authorization, MerchantCode, Hash } = getRequestData();
      const resultingUrl = `${baseUrl}Auth/refresh`;

      let encryptedPayload: any = {
        refreshToken: refreshToken
      };

      if (Hash && MerchantCode) {
        try {
          encryptedPayload = await EncryptionHelper.encrypt(
            JSON.stringify({ refreshToken: refreshToken }),
            Hash,
            MerchantCode
          );
        } catch (encryptErr) {
          console.error("Encryption failed for refresh token request:", encryptErr);
        }
      }

      const res = await axios.post(resultingUrl, encryptedPayload, {
        headers: {
          "Content-Type": "application/json",
          MerchantCode,
          MerchantHash: Hash,
          Authorization,
        }
      });

      if (res.status === 200 && res.data) {
        let decryptedData = res.data;
        if (Hash && MerchantCode) {
          try {
            decryptedData = await EncryptionHelper.decrypt(res.data, Hash, MerchantCode);
          } catch (decryptErr) {
            // Not encrypted or decryption failed
          }
        }

        const parsed = typeof decryptedData === "string" ? JSON.parse(decryptedData) : decryptedData;
        const newAccessToken = parsed?.AccessToken || parsed?.data?.AccessToken || parsed?.token;
        const newRefreshToken = parsed?.RefreshToken || parsed?.data?.RefreshToken || parsed?.refreshToken;

        if (newAccessToken) {
          const currentUser = useAuthStore.getState().b2CUser || {};
          useAuthStore.getState().setB2cAuth(newAccessToken, currentUser, newRefreshToken || refreshToken);

          // Encrypt the tokens before posting to parent and send it to parent
          const tokenPayload = await EncryptionHelper.encrypt(
            JSON.stringify({ accessToken: newAccessToken, refreshToken: newRefreshToken || refreshToken }),
            Hash,
            MerchantCode,
          );
          window.parent.postMessage({ type: 'TRIPS_TOKEN_REFRESH', data: tokenPayload }, '*');


          resolve(newAccessToken);
          isRefreshing = false;
          refreshQueue.forEach((cb) => cb(newAccessToken));
          refreshQueue = [];
          return;
        }
      }
    } catch (err) {
      console.error("Token refresh failed:", err);
    }

    useAuthStore.getState().logout();
    // redirectHome();
    resolve(null);
    isRefreshing = false;
    refreshQueue.forEach((cb) => cb(null));
    refreshQueue = [];
  });
};

export const getAPI = async (url: string) => {
  const {
    baseUrl, Authorization, MerchantCode, Hash,
  } = getRequestData();
  const resultingUrl = `${baseUrl}${url}`;
  const accessToken = useAuthStore.getState().accessToken;

  try {
    const res = await axios.get(resultingUrl, {
      headers: {
        MerchantCode,
        MerchantHash: Hash,
        Authorization,
        ...(accessToken ? { B2CAuthorization: `Bearer ${accessToken}` } : {}),
      },
    });
   

    return { status: 200, data: res.data };
  } catch (e: any) {
    if (axios.isAxiosError(e) && e.response?.status === 401 && accessToken) {
      const refreshedToken = await handleB2cTokenRefresh();
      if (refreshedToken) {
        try {
          const res = await axios.get(resultingUrl, {
            headers: {
              MerchantCode,
              MerchantHash: Hash,
              Authorization,
              B2CAuthorization: `Bearer ${refreshedToken}`,
            },
          });
          return { status: 200, data: res.data };
        } catch (retryError) {
          return processAPIError(retryError);
        }
      }
    }
    return processAPIError(e);
  }
};

export const postAPI = async (url: string, payload: any) => {
  const {
    baseUrl, Authorization, MerchantCode, Hash,
  } = getRequestData();
  const resultingUrl = `${baseUrl}${url}`;
  const accessToken = useAuthStore.getState().accessToken;

  try {
    const res = await axios.post(resultingUrl, payload, {
      headers: {
        MerchantCode,
        MerchantHash: Hash,
        Authorization,
        ...(accessToken ? { B2CAuthorization: `Bearer ${accessToken}` } : {}),
      },
    });

    return { status: 200, data: res.data };
  } catch (e: any) {
    if (axios.isAxiosError(e) && e.response?.status === 401 && accessToken) {
      const refreshedToken = await handleB2cTokenRefresh();
      if (refreshedToken) {
        try {
          const res = await axios.post(resultingUrl, payload, {
            headers: {
              MerchantCode,
              MerchantHash: Hash,
              Authorization,
              B2CAuthorization: `Bearer ${refreshedToken}`,
            },
          });
          return { status: 200, data: res.data };
        } catch (retryError) {
          return processAPIError(retryError);
        }
      }
    }
    return processAPIError(e);
  }
};

export const encryptedPostAPI = async (url: string, payload: any) => {
  const {
    baseUrl, Authorization, MerchantCode, Hash,
  } = getRequestData();
  const resultingUrl = `${baseUrl}${url}`;
  const accessToken = useAuthStore.getState().accessToken;

  try {
    const res = await axios.post(resultingUrl, payload, {
      headers: {
        "Content-Type": "application/json",
        MerchantCode,
        MerchantHash: Hash,
        Authorization,
        ...(accessToken ? { B2CAuthorization: `Bearer ${accessToken}` } : {}),
      },
    });

    return { status: 200, data: res.data };
  } catch (e: any) {
    if (axios.isAxiosError(e) && e.response?.status === 401 && accessToken) {
      const refreshedToken = await handleB2cTokenRefresh();
      if (refreshedToken) {
        try {
          const res = await axios.post(resultingUrl, payload, {
            headers: {
              "Content-Type": "application/json",
              MerchantCode,
              MerchantHash: Hash,
              Authorization,
              B2CAuthorization: `Bearer ${refreshedToken}`,
            },
          });
          return { status: 200, data: res.data };
        } catch (retryError) {
          return processAPIError(retryError);
        }
      }
    }
    return processAPIError(e);
  }
};

export const getAPICustom = async (url: string, payload: any, header: any) => {
  const resultingUrl = `${BASE_API_URL}${url}`;

  try {
    const res = await axios.get(resultingUrl, {
      headers: header,
      ...(payload ? { params: payload } : {}),
    });

    return { status: 200, data: res.data };
  } catch (e: any) {
    return processAPIError(e);
  }
};


export const getAirlineApi = async (url: string) => {
  const resultingUrl = `${AIRLINE_BASE_API_URL}${url}`;

  try {
    const res = await axios.get(resultingUrl);

    return { status: 200, data: res.data };
  } catch (e: any) {
    return processAPIError(e);
  }
};

export const uploadPassportOcrApi = async (payload: FormData) => {
  const resultingUrl = `${OCR_UPLOAD_URL}`;

  try {
    const res = await axios.post(resultingUrl, payload);

    return { status: 200, data: res.data };
  } catch (e: any) {
    return processAPIError(e);
  }
};
